import { Badge, Box, Divider, Flex, Text, VStack } from '@chakra-ui/react'
import { CervicalExamData, FieldMapValue, PrenatalExamData } from '@hb/shared'
import { SectionLabel } from '../../SectionLabel'

const ExamRow = ({ label, notes }: { label: string; notes: string | undefined }) => {
  if (!notes) return null
  return (
    <Flex gap={2} align="flex-start">
      <Text fontSize="xs" fontWeight={600} color="gray.500" w="140px" flexShrink={0}>
        {label}
      </Text>
      <Text fontSize="sm">{notes}</Text>
    </Flex>
  )
}

const getBishopScore = (exam: Partial<CervicalExamData>): number | null => {
  const { dilation, effacement, station, consistency, position } = exam
  if (
    dilation == null &&
    effacement == null &&
    station == null &&
    consistency == null &&
    position == null
  )
    return null
  let score = 0
  if (typeof dilation === 'number') {
    if (dilation >= 5) score += 3
    else if (dilation >= 3) score += 2
    else if (dilation >= 1) score += 1
  }
  if (typeof effacement === 'number') {
    if (effacement >= 80) score += 3
    else if (effacement >= 60) score += 2
    else if (effacement >= 40) score += 1
  }
  if (typeof station === 'number') {
    if (station >= 1) score += 3
    else if (station >= -1) score += 2
    else if (station === -2) score += 1
  }
  if (consistency === 'soft') score += 2
  else if (consistency === 'medium') score += 1
  if (position === 'anterior') score += 2
  else if (position === 'mid') score += 1
  return score
}

const bishopLabel = (score: number) => {
  if (score >= 9) return { label: 'Favorable', color: 'green' }
  if (score >= 6) return { label: 'Moderate', color: 'orange' }
  return { label: 'Unfavorable', color: 'red' }
}

const CervicalExamView = ({ exam }: { exam: CervicalExamData }) => {
  const bishop = getBishopScore(exam)
  return (
    <Box
      mt={2}
      pl={3}
      borderLeft="2px solid"
      borderColor="blue.200"
      _dark={{ borderColor: 'blue.700' }}>
      <Text fontSize="xs" fontWeight={600} color="blue.600" mb={1}>
        Cervical Exam
      </Text>
      <VStack align="flex-start" spacing={0.5}>
        {exam.membraneStatus && (
          <Flex gap={2} align="center">
            <Text fontSize="xs" color="gray.500" w="120px" flexShrink={0}>
              Membrane Status
            </Text>
            <Badge
              colorScheme={
                exam.membraneStatus === 'intact'
                  ? 'green'
                  : exam.membraneStatus === 'bulging'
                    ? 'orange'
                    : 'red'
              }
              fontSize="2xs">
              {exam.membraneStatus.charAt(0).toUpperCase() + exam.membraneStatus.slice(1)}
            </Badge>
          </Flex>
        )}
        {exam.dilation != null && (
          <Flex gap={2}>
            <Text fontSize="xs" color="gray.500" w="120px" flexShrink={0}>
              Dilation
            </Text>
            <Text fontSize="sm">{exam.dilation} cm</Text>
          </Flex>
        )}
        {exam.effacement != null && (
          <Flex gap={2}>
            <Text fontSize="xs" color="gray.500" w="120px" flexShrink={0}>
              Effacement
            </Text>
            <Text fontSize="sm">{exam.effacement}%</Text>
          </Flex>
        )}
        {exam.station != null && (
          <Flex gap={2}>
            <Text fontSize="xs" color="gray.500" w="120px" flexShrink={0}>
              Station
            </Text>
            <Text fontSize="sm">{exam.station > 0 ? `+${exam.station}` : exam.station}</Text>
          </Flex>
        )}
        {exam.consistency && (
          <Flex gap={2}>
            <Text fontSize="xs" color="gray.500" w="120px" flexShrink={0}>
              Consistency
            </Text>
            <Text fontSize="sm" textTransform="capitalize">
              {exam.consistency}
            </Text>
          </Flex>
        )}
        {exam.position && (
          <Flex gap={2}>
            <Text fontSize="xs" color="gray.500" w="120px" flexShrink={0}>
              Position
            </Text>
            <Text fontSize="sm" textTransform="capitalize">
              {exam.position}
            </Text>
          </Flex>
        )}
        {bishop != null && (
          <Flex align="center" gap={2} pt={1}>
            <Text fontSize="xs" fontWeight={600} color="gray.600">
              Bishop Score:
            </Text>
            <Badge colorScheme={bishopLabel(bishop).color} fontSize="sm" px={2}>
              {bishop} — {bishopLabel(bishop).label}
            </Badge>
          </Flex>
        )}
      </VStack>
    </Box>
  )
}

// §2.6: Returns null when exam was not documented — spec explicitly prohibits
// "not documented" or "not performed" as default text.
export const PhysicalExamView = ({ data }: { data: FieldMapValue }) => {
  const prenatal = data as PrenatalExamData
  if (!prenatal.hasPhysicalExam) return null

  const physical = data?.physical as Record<string, { notes?: string }> | undefined

  return (
    <Box w="100%">
      <SectionLabel>Physical Exam</SectionLabel>
      <VStack align="flex-start" spacing={1}>
        <ExamRow label="General Appearance" notes={physical?.generalAppearance?.notes} />
        <ExamRow label="Heart" notes={physical?.heart?.notes} />
        <ExamRow label="Lungs" notes={physical?.lungs?.notes} />
        <ExamRow label="Abdomen / Uterus" notes={physical?.abdomen?.notes} />
        <ExamRow label="HEENT" notes={physical?.heent?.notes} />
        <ExamRow label="Neck" notes={physical?.neck?.notes} />
        <ExamRow label="Skin" notes={physical?.skin?.notes} />
        <ExamRow label="Breast" notes={physical?.breast?.notes} />
        <ExamRow label="Neuro" notes={physical?.neuro?.notes} />
        <ExamRow label="Extremities" notes={physical?.extremities?.notes} />
        <ExamRow label="Additional Findings" notes={(data as any)?.physicalAdditional} />
      </VStack>
      {prenatal.hasCervicalExam && prenatal.cervicalExam ? (
        <>
          <Divider my={2} />
          <CervicalExamView exam={prenatal.cervicalExam} />
        </>
      ) : null}
    </Box>
  )
}

const PELVIC_LABELS: Record<string, string> = {
  vulva: 'Vulva',
  vaginalPink: 'Vaginal / Rugae',
  cervix: 'Cervix',
  uterus: 'Uterus',
  adnexa: 'Adnexa',
  rectum: 'Rectum',
  pelvis: 'Pelvis',
}

// §2.6: Returns null when pelvic exam was not documented.
export const PelvicExamView = ({ data }: { data: FieldMapValue }) => {
  const prenatal = data as PrenatalExamData
  if (!prenatal.hasPelvicExam) return null

  const pelvic = data?.pelvic as Record<string, { notes?: string }> | undefined

  const hasAnyFindings = pelvic ? Object.values(pelvic).some(v => v?.notes) : false

  if (!hasAnyFindings) return null

  return (
    <Box w="100%">
      <SectionLabel>Pelvic Exam</SectionLabel>
      <VStack align="flex-start" spacing={1}>
        {Object.entries(PELVIC_LABELS).map(([key, label]) => (
          <ExamRow key={key} label={label} notes={pelvic?.[key]?.notes} />
        ))}
      </VStack>
    </Box>
  )
}
